import { z, type ZodSchema } from 'zod';

export const LOCAL_STORAGE_KEY = 'feature-flags.overrides';

export type FeatureFlagDefinition = {
  name: string;
  schema: ZodSchema;
};

export type FeatureFlagInput<S extends ZodSchema> = z.input<S>;
export type FeatureFlagOutput<S extends ZodSchema> = z.infer<S>;

function localStorageOverrides() {
  const value = window.localStorage.getItem(LOCAL_STORAGE_KEY);

  if (typeof value !== 'string') {
    return null;
  }

  try {
    const parsed = JSON.parse(value);

    // first make sure this is an object
    const object = z.record(z.unknown()).safeParse(parsed);
    if (object.success === false) {
      return null;
    }
    return object.data as Overrides<string, ZodSchema>;
  } catch {}

  return null;
}

type Overrides<K extends string, S extends ZodSchema> = Record<
  K,
  FeatureFlagInput<S>
>;

function getRawValue<K extends string, S extends ZodSchema>({
  flagName,
  serverValue,
  overrides,
}: {
  flagName: K;
  overrides: Overrides<K, S> | null;
  serverValue?: string;
}) {
  if (overrides !== null && flagName in overrides) {
    return overrides[flagName];
  }

  return serverValue;
}

export function resolveUserFeatureFlags<K extends string, D extends ZodSchema>(
  flagName: K,
  schema: D,
  serverValue: string | undefined
): z.infer<D> {
  const overrides = localStorageOverrides();
  const rawValue = getRawValue({
    flagName,
    serverValue,
    overrides,
  });
  const parsedValue = schema.safeParse(rawValue);
  if (parsedValue.success === false) {
    return false;
  }

  return parsedValue.data;
}
